import { Link } from "react-router-dom";
import { ArrowLeft, Smartphone, Mail, Trash2, Clock, Shield, AlertTriangle, Phone } from "lucide-react";
import useSeo from "@/hooks/useSeo";

export default function FinusDeleteAccount() {
  useSeo({
    title: "Delete Account — FINUS by Yugiq",
    description: "Request deletion of your FINUS account and all associated data. Learn how to delete your account from within the app or via email.",
    canonical: "https://yugiq.in/finus/delete-account",
  });

  return (
    <main className="min-h-screen bg-white text-[#0F0F1A]" data-testid="finus-delete-page">

      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/finus" className="flex items-center gap-2" data-testid="delete-logo">
            <span className="w-8 h-8 rounded-[9px] overflow-hidden">
              <img src="/logo.png" alt="Yugiq" className="w-full h-full object-cover" />
            </span>
            <span className="font-extrabold text-[15px] text-[#0F0F1A]">FINUS</span>
          </Link>
          <Link to="/finus" className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#6C3AED] border border-[#6C3AED]/30 rounded-full px-4 py-1.5 hover:bg-[#6C3AED]/5 transition-colors" data-testid="delete-back-btn">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to FINUS
          </Link>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-14 sm:py-20 space-y-10" data-testid="delete-content">

        {/* Header */}
        <div className="border-b border-gray-100 pb-10">
          <div className="inline-flex items-center gap-2 bg-red-50 border border-red-100 rounded-full px-3.5 py-1.5 mb-5">
            <Trash2 className="w-3.5 h-3.5 text-red-500" />
            <span className="text-[12px] font-semibold text-red-500 tracking-wide uppercase">Account Deletion</span>
          </div>
          <h1 className="text-[36px] sm:text-[48px] font-black tracking-tight leading-tight text-[#0F0F1A]">
            Finus – Account<br />Deletion Request
          </h1>
          <p className="mt-4 text-[16px] text-gray-500 leading-relaxed max-w-xl">
            If you would like to delete your Finus account and all associated data, please follow the steps below.
          </p>
        </div>

        {/* Option 1 */}
        <Section icon={Smartphone} iconBg="bg-[#6C3AED]/8" iconColor="text-[#6C3AED]" label="Option 1 — Recommended" title="Delete from within the app">
          <ol className="space-y-3">
            {["Open the Finus app", "Go to Settings", "Tap Delete Account", "Confirm deletion"].map((step, i) => (
              <li key={step} className="flex items-center gap-3 text-[15px] text-gray-600">
                <span className="w-6 h-6 rounded-full bg-[#6C3AED]/10 text-[#6C3AED] text-[12px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                {step}
              </li>
            ))}
          </ol>
        </Section>

        {/* Option 2 */}
        <Section icon={Mail} iconBg="bg-gray-100" iconColor="text-gray-500" label="Option 2 — Manual Request" title="Email us if you can't access the app">
          <p className="text-[15px] text-gray-600 mb-4">Send an email to:</p>
          <a href="mailto:hello@yugiq.in?subject=Account%20Deletion%20Request" className="inline-flex items-center gap-2 text-[15px] font-semibold text-[#6C3AED] hover:underline" data-testid="delete-email-link">
            <Mail className="w-4 h-4" /> hello@yugiq.in
          </a>
          <div className="mt-4 bg-gray-50 rounded-xl p-4 space-y-2">
            <p className="text-[13px] font-semibold text-gray-500 uppercase tracking-wide">Include in your email:</p>
            {["Your registered phone number", "Subject line: Account Deletion Request"].map((item) => (
              <div key={item} className="flex items-center gap-2 text-[14px] text-gray-600">
                <span className="w-1.5 h-1.5 rounded-full bg-[#6C3AED] shrink-0" />
                {item}
              </div>
            ))}
          </div>
        </Section>

        {/* What gets deleted */}
        <Section icon={Trash2} iconBg="bg-red-50" iconColor="text-red-500" label="Data Deletion" title="What data will be deleted">
          <div className="grid sm:grid-cols-2 gap-2">
            {[
              "Your phone number",
              "All expenses and income data",
              "Budgets and goals",
              "Notes and transaction history",
              "Any linked partner data",
            ].map((item) => (
              <div key={item} className="flex items-center gap-2 text-[14px] text-gray-600 bg-red-50/60 border border-red-100/80 rounded-xl px-3 py-2.5">
                <Trash2 className="w-3.5 h-3.5 text-red-400 shrink-0" />
                {item}
              </div>
            ))}
          </div>
        </Section>

        {/* Retention */}
        <Section icon={Clock} iconBg="bg-amber-50" iconColor="text-amber-500" label="Data Retention" title="When is data removed?">
          <div className="space-y-3">
            <Row dot="bg-green-400" text="Your data is deleted immediately upon confirmation" />
            <Row dot="bg-amber-400" text="Backup systems may retain data for up to 7 days, after which it is permanently removed" />
          </div>
        </Section>

        {/* Retained data */}
        <Section icon={Shield} iconBg="bg-blue-50" iconColor="text-blue-500" label="Exceptions" title="Data that may be retained">
          <p className="text-[15px] text-gray-500 mb-3">We may retain minimal data if required for:</p>
          <div className="space-y-2">
            <Row dot="bg-blue-400" text="Legal compliance" />
            <Row dot="bg-blue-400" text="Fraud prevention" />
          </div>
        </Section>

        {/* Important notes */}
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex gap-4" data-testid="delete-warning">
          <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="space-y-2">
            <p className="font-bold text-[15px] text-[#0F0F1A]">Important Notes</p>
            {[
              "Account deletion is permanent and irreversible",
              "You will lose access to all your data",
              "Subscriptions must be cancelled separately via Google Play Store",
            ].map((note) => (
              <div key={note} className="flex items-start gap-2 text-[14px] text-amber-800">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0 mt-1.5" />
                {note}
              </div>
            ))}
          </div>
        </div>

        {/* Contact */}
        <Section icon={Phone} iconBg="bg-[#6C3AED]/8" iconColor="text-[#6C3AED]" label="Contact" title="Still have questions?">
          <p className="text-[15px] text-gray-500 mb-3">Reach out and we'll help you out.</p>
          <a href="mailto:hello@yugiq.in" className="inline-flex items-center gap-2 text-[15px] font-semibold text-[#6C3AED] hover:underline" data-testid="delete-contact-email">
            <Mail className="w-4 h-4" /> hello@yugiq.in
          </a>
        </Section>

      </div>

      {/* Footer */}
      <footer className="py-8 border-t border-gray-100 mt-6">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <span className="text-[11px] tracking-[0.2em] uppercase text-gray-300 font-semibold">© {new Date().getFullYear()} FINUS · A Yugiq product</span>
          <Link to="/finus" className="text-[12px] font-semibold text-[#6C3AED] hover:underline">← Back to FINUS</Link>
        </div>
      </footer>
    </main>
  );
}

function Section({ icon: Icon, iconBg, iconColor, label, title, children }) {
  return (
    <div className="space-y-4" data-testid={`delete-section-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${iconBg}`}>
          <Icon className={`w-4.5 h-4.5 ${iconColor}`} />
        </div>
        <div>
          <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-gray-300">{label}</div>
          <h2 className="text-[18px] font-extrabold text-[#0F0F1A] leading-tight">{title}</h2>
        </div>
      </div>
      <div className="pl-12">{children}</div>
    </div>
  );
}

function Row({ dot, text }) {
  return (
    <div className="flex items-start gap-2.5 text-[14px] text-gray-600">
      <span className={`w-2 h-2 rounded-full ${dot} shrink-0 mt-1.5`} />
      {text}
    </div>
  );
}
